import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Modal } from "reactstrap"

/** Zonas padre de la sucursal, que son las que admiten sub-zonas. */
const zonasPadre = regions => (regions || []).filter(region => !region.parent_id)

/**
 * Construye el borrador: por cada zona padre, sus sub-zonas actuales con los
 * precios que el formulario tenga capturados para ellas.
 */
const construirBorrador = (regions, prices) => {
  const borrador = {}
  zonasPadre(regions).forEach(padre => {
    borrador[padre.id] = (regions || [])
      .filter(region => region.parent_id === padre.id)
      .map(region => {
        const precio = prices[region.id] || {}
        return {
          key: `z${region.id}`,
          id: region.id,
          nombre: region.name,
          destino: precio.destino || "",
          granja: precio.granja || "",
        }
      })
  })
  return borrador
}

let nextKey = 1

const SubZonesModal = ({
  isOpen,
  regions,
  prices,
  hasLocal,
  onToggle,
  onSave,
  onApplyPrices,
}) => {
  const [zonaId, setZonaId] = useState(null)
  const [borrador, setBorrador] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  const padres = zonasPadre(regions)

  useEffect(() => {
    if (!isOpen) return
    setZonaId(padres[0] ? padres[0].id : null)
    setBorrador(construirBorrador(regions, prices))
    setIsSaving(false)
    // eslint-disable-next-line
  }, [isOpen])

  const lista = borrador[zonaId] || []
  const zona = padres.find(region => region.id === zonaId)

  const editarLista = transformar =>
    setBorrador(previo => ({
      ...previo,
      [zonaId]: transformar(previo[zonaId] || []),
    }))

  const onChangeCampo = (key, campo, value) =>
    editarLista(items =>
      items.map(item => (item.key === key ? { ...item, [campo]: value } : item))
    )

  const onAdd = () => {
    if (!zonaId) return
    editarLista(items => [
      ...items,
      { key: `n${nextKey++}`, nombre: "", destino: "", granja: "" },
    ])
  }

  const onRemove = key =>
    editarLista(items => items.filter(item => item.key !== key))

  const onAceptar = async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      // solo se envían las sub-zonas con nombre; el endpoint crea las nuevas y
      // renombra las existentes, pero no borra
      const payload = []
      Object.keys(borrador).forEach(parentId => {
        borrador[parentId]
          .filter(item => item.nombre.trim())
          .forEach(item =>
            payload.push({
              ...(item.id ? { id: item.id } : {}),
              name: item.nombre.trim(),
              parent_id: Number(parentId),
            })
          )
      })

      const actualizadas = await onSave(payload)
      onApplyPrices(mapaDePrecios(borrador, actualizadas))
      onToggle()
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
  }

  const claseFila = `av-sz-grid ${hasLocal ? "has-local" : "no-local"}`

  return (
    <Modal isOpen={isOpen} backdrop="static" toggle={onToggle} centered>
      <div className="av-sz">
        <div className="av-sz-head">
          <h3>Actualizar Sub-zonas</h3>
          <button type="button" className="av-btn-icon" onClick={onToggle}>
            <i className="bx bx-x" />
          </button>
        </div>

        <div className="av-sz-zone">
          <label className="av-label" htmlFor="av-sz-zona">
            Zona
          </label>
          <select
            id="av-sz-zona"
            className="av-field"
            value={zonaId || ""}
            onChange={event => setZonaId(Number(event.target.value))}
          >
            {padres.length === 0 && <option value="">Sin zonas</option>}
            {padres.map(region => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>

        <div className="av-sz-body">
          <div className="av-sz-banner">
            Sub-zonas para {zona ? zona.name : "—"}
          </div>
          <div className="av-sz-box">
            {lista.length === 0 ? (
              <div className="av-sz-empty">Sin sub-zonas registradas</div>
            ) : (
              <>
                <div className={claseFila}>
                  <div className="av-truck-head">Nombre</div>
                  <div className="av-truck-head">P. Destino</div>
                  {hasLocal && <div className="av-truck-head">P. Granja</div>}
                  <div />
                </div>

                {lista.map(item => (
                  <div className={claseFila} key={item.key}>
                    <input
                      type="text"
                      className="av-sz-input"
                      placeholder="Nombre de la sub-zona"
                      value={item.nombre}
                      onChange={event =>
                        onChangeCampo(item.key, "nombre", event.target.value)
                      }
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="av-sz-input av-num"
                      placeholder="0.00"
                      value={item.destino}
                      onChange={event =>
                        onChangeCampo(item.key, "destino", event.target.value)
                      }
                    />
                    {hasLocal && (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="av-sz-input av-num"
                        placeholder="0.00"
                        value={item.granja}
                        onChange={event =>
                          onChangeCampo(item.key, "granja", event.target.value)
                        }
                      />
                    )}
                    {item.id ? (
                      // una sub-zona ya guardada no se puede eliminar: el
                      // endpoint de sub-zonas solo crea y renombra
                      <span />
                    ) : (
                      <button
                        type="button"
                        className="av-btn-icon"
                        title="Quitar sub-zona"
                        onClick={() => onRemove(item.key)}
                      >
                        <i className="bx bx-x" />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}

            <button
              type="button"
              className="av-btn-dashed mt-1"
              disabled={!zonaId}
              onClick={onAdd}
            >
              <i className="bx bx-plus" />
              Agregar sub-zona
            </button>
          </div>
        </div>

        <div className="av-sz-actions">
          <button
            type="button"
            className="av-btn-soft"
            disabled={isSaving}
            onClick={onToggle}
          >
            Cerrar
          </button>
          <button
            type="button"
            className={`av-btn-save${isSaving ? " is-saving" : ""}`}
            disabled={isSaving}
            onClick={onAceptar}
          >
            <i className="bx bx-check" /> {isSaving ? "Guardando…" : "Aceptar"}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Empareja los precios capturados con el id definitivo de cada sub-zona: las
 * existentes ya lo traen, las nuevas se localizan por nombre dentro de su zona.
 */
function mapaDePrecios(borrador, regions) {
  const mapa = {}
  Object.keys(borrador).forEach(parentId => {
    borrador[parentId].forEach(item => {
      if (!item.nombre.trim()) return
      const region =
        (regions || []).find(
          candidata =>
            (item.id && candidata.id === item.id) ||
            (!item.id &&
              Number(candidata.parent_id) === Number(parentId) &&
              candidata.name === item.nombre.trim())
        ) || null
      if (!region) return
      mapa[region.id] = { destino: item.destino, granja: item.granja }
    })
  })
  return mapa
}

SubZonesModal.propTypes = {
  isOpen: PropTypes.bool,
  regions: PropTypes.array.isRequired,
  prices: PropTypes.object.isRequired,
  hasLocal: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onApplyPrices: PropTypes.func.isRequired,
}

export default SubZonesModal
